import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Gift, Flame, Loader2 } from 'lucide-react';
import { faucetApi } from '../../api';
import { useAppStore } from '../../store/useAppStore';
import { useConfigStore } from '../../store/useConfigStore';
import { useTelegram } from '../../hooks';
import { showCombinedInterstitialAds } from '../../hooks/useCombinedAds';
import toast from 'react-hot-toast';

export function FaucetClaim() {
  const [canClaim, setCanClaim] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [streak, setStreak] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [lastReward, setLastReward] = useState(0);
  const [showingAd, setShowingAd] = useState(false);

  const updateBalance = useAppStore((state) => state.updateBalance);
  const user = useAppStore((state) => state.user);
  const faucetConfig = useConfigStore((state) => state.faucet);
  const { hapticFeedback, hapticNotification } = useTelegram();

  const fetchStatus = useCallback(async () => {
    try {
      const status = await faucetApi.getStatus();
      setCanClaim(status.canClaim);
      setCooldown(status.cooldownRemaining);
      setStreak(status.streak);
    } catch (error) {
      console.error('Failed to fetch faucet status:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Countdown timer
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            setCanClaim(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldown]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClaim = async () => {
    if (!canClaim || isLoading || showingAd) return;

    // Show combined interstitial ads before claiming (AdsGram + Onclicka)
    setShowingAd(true);
    try {
      toast.loading('Showing ads...', { id: 'faucet-ads' });
      await showCombinedInterstitialAds('faucet_claim');
      toast.dismiss('faucet-ads');
    } catch (e) {
      toast.dismiss('faucet-ads');
      console.log('Ads not available, proceeding with claim');
    }
    setShowingAd(false);

    setIsLoading(true);
    hapticFeedback('medium');

    try {
      const result = await faucetApi.claim();

      if (result.success && result.amount) {
        hapticNotification('success');
        setLastReward(result.amount);
        setShowReward(true);
        setCanClaim(false);
        setCooldown(faucetConfig.hourlyClaimCooldown); // Use config value
        setStreak(result.streak || streak + 1);

        // Update balance using server-returned value (authoritative)
        if (result.newBalance !== undefined) {
          updateBalance(result.newBalance);
        } else if (user) {
          // Fallback to local calculation only if server doesn't return balance
          updateBalance(user.balance + result.amount + (result.streakBonus || 0));
        }

        toast.success(`+${result.amount} points claimed!`);

        // Hide reward animation after delay
        setTimeout(() => setShowReward(false), 2000);
      }
    } catch (error: any) {
      hapticNotification('error');
      toast.error(error.response?.data?.error || 'Failed to claim');
      
      if (error.response?.data?.cooldownRemaining) {
        setCooldown(error.response.data.cooldownRemaining);
        setCanClaim(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="card p-6 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10 pointer-events-none" />

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Hourly Faucet</h3>
              <p className="text-white/60 text-sm">Claim free points every hour</p>
            </div>
          </div>

          {/* Streak badge */}
          {streak > 0 && (
            <div className="flex items-center gap-1 bg-orange-500/20 px-3 py-1 rounded-full">
              <Flame className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 font-medium text-sm">{streak} day</span>
            </div>
          )}
        </div>

        {/* Claim button */}
        <motion.button
          whileTap={{ scale: canClaim ? 0.95 : 1 }}
          onClick={handleClaim}
          disabled={!canClaim || isLoading || showingAd}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
            canClaim && !showingAd
              ? 'btn-primary animate-pulse-glow'
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
        >
          {showingAd ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Loading Ad...
            </span>
          ) : isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Claiming...
            </span>
          ) : canClaim ? (
            <span className="flex items-center justify-center gap-2">
              <Gift className="w-5 h-5" />
              Claim Now ({faucetConfig.hourlyClaimMin}-{faucetConfig.hourlyClaimMax} pts)
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <Clock className="w-5 h-5" />
              Next claim in {formatTime(cooldown)}
            </span>
          )}
        </motion.button>

        {/* Reward animation */}
        <AnimatePresence>
          {showReward && (
            <motion.div
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: -50 }}
              exit={{ opacity: 0, y: -100 }}
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <span className="text-4xl font-bold text-green-400">
                +{lastReward}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
